// src/helpers/video/generateClientThumbs.ts


export type ClientFrame = {
  time: number
  url: string
}

export type ClientThumbs = {
  thumbs: ClientFrame[]
  scrubFrames: ClientFrame[]
}

type GenerateClientThumbsOptions = {
  maxThumbs?: number
  maxWidth?: number
  maxHeight?: number
  quality?: number
  onFrame?: (frame: ClientFrame, index: number, total: number) => void
}

type ThumbPlan = { times: number[] }

type FrameTarget = {
  time: number
  key: string
  isCover: boolean
  isScrub: boolean
}

/*
 * -------------------------------------------------------
 * TUNING KNOBS
 * -------------------------------------------------------
 *
 * Cover thumbs stay at 8 by default and preserve the
 * existing backend-matching timestamp plan.
 *
 * Scrub frames are dynamic and include those cover frames,
 * so extra scrub frames are the only additional seeks.
 */

const getDeviceProfile = () => {
  const nav = navigator as Navigator & { deviceMemory?: number }

  return {
    memoryGB: nav.deviceMemory ?? null,
    cores: navigator.hardwareConcurrency ?? null
  }
}
const isLikelyLowPowerDevice = () => {
  const { memoryGB, cores } = getDeviceProfile()

  return (
    (memoryGB !== null && memoryGB <= 4) ||
    (cores !== null && cores <= 4)
  )
}

const is4kOrHigher = (width: number, height: number) =>
  width >= 3840 || height >= 2160


const CLIENT_THUMB_CONFIG = {
  coverThumbs: 8,

  output: {
    maxWidth: 1280,
    maxHeight: 720,
    quality: 0.78
  },

  scrub: {
    minFrames: 8,
    maxFrames: 72,

    durationBuckets: [
      { maxSeconds: 30, frames: 14 },
      { maxSeconds: 60, frames: 24 },
      { maxSeconds: 120, frames: 30 },
      { maxSeconds: 180, frames: 36 },
      { maxSeconds: 300, frames: 42 },
      { maxSeconds: 600, frames: 50 },
      { maxSeconds: 900, frames: 56 },
      { maxSeconds: 1200, frames: 62 },
      { maxSeconds: 1800, frames: 68 },
      { maxSeconds: Infinity, frames: 72 }
    ],

    lowPower4kBuckets: [
      { maxSeconds: 30, frames: 12 },
      { maxSeconds: 60, frames: 24 },
      { maxSeconds: 120, frames: 28 },
      { maxSeconds: 180, frames: 32 },
      { maxSeconds: 300, frames: 36 },
      { maxSeconds: 600, frames: 40 },
      { maxSeconds: 900, frames: 44 },
      { maxSeconds: 1200, frames: 46 },
      { maxSeconds: Infinity, frames: 48 }
    ],

resolutionBonus: {
  max1080p: 4,
  max1440p: 2,
  max4k: -4,
  above4k: -4
}
  },

  seek: {
    eventTimeoutMs: 15000,
    equalityToleranceSec: 0.02,
    endPaddingSec: 0.05
  },

  retry: {
    attempts: 2,
    delayMs: 75
  },

  metadataTimeoutMs: 15000
} as const

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value))

const timeKey = (time: number) => time.toFixed(6)

const waitForEvent = async (
  target: EventTarget,
  eventName: string,
  timeoutMs = 10000
) =>
  await new Promise<void>((resolve, reject) => {
    let timer: ReturnType<typeof setTimeout> | null = null

    const cleanup = () => {
      target.removeEventListener(eventName, handleSuccess)
      target.removeEventListener('error', handleError)
      if (timer) clearTimeout(timer)
    }

    const handleSuccess = () => {
      cleanup()
      resolve()
    }

    const handleError = () => {
      cleanup()
      reject(new Error(`Failed waiting for ${eventName}`))
    }

    target.addEventListener(eventName, handleSuccess, { once: true })
    target.addEventListener('error', handleError, { once: true })

    timer = setTimeout(() => {
      cleanup()
      reject(new Error(`Timed out waiting for ${eventName}`))
    }, timeoutMs)
  })

const seekVideo = async (video: HTMLVideoElement, time: number) => {
  const { equalityToleranceSec, endPaddingSec, eventTimeoutMs } =
    CLIENT_THUMB_CONFIG.seek

  const safeTime = clamp(
    time,
    0,
    Math.max(0, video.duration - endPaddingSec)
  )

  if (Math.abs(video.currentTime - safeTime) < equalityToleranceSec) return

  video.currentTime = safeTime
  await waitForEvent(video, 'seeked', eventTimeoutMs)
}

const getOutputSize = (
  sourceWidth: number,
  sourceHeight: number,
  maxWidth: number,
  maxHeight: number
) => {
  const scale = Math.min(
    maxWidth / sourceWidth,
    maxHeight / sourceHeight,
    1
  )

  return {
    width: Math.max(1, Math.round(sourceWidth * scale)),
    height: Math.max(1, Math.round(sourceHeight * scale))
  }
}

const canvasToBlob = async (canvas: HTMLCanvasElement, quality: number) =>
  await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(blob => {
      if (!blob) {
        reject(new Error('Failed to create thumbnail blob'))
        return
      }

      resolve(blob)
    }, 'image/jpeg', quality)
  })

/*
 * Keep this cover plan unchanged.
 * Mirrors PostEncodeJob::computeThumbPlan($duration, 8).
 */
const computeCoverThumbPlan = (
  duration: number,
  maxThumbs: number = CLIENT_THUMB_CONFIG.coverThumbs
): ThumbPlan => {
  const safeDuration = Math.floor(Math.max(0, duration))

  if (safeDuration <= 0) return { times: [1] }

  const end = Math.max(0, safeDuration - 0.001)

  if (safeDuration >= maxThumbs) {
    const intervalSec = Math.max(
      1,
      Math.floor(safeDuration / (maxThumbs + 1))
    )

    return {
      times: Array.from(
        { length: maxThumbs },
        (_, index) => Math.min((index + 1) * intervalSec, end)
      )
    }
  }

  const times: number[] = []

  for (let time = 1; time <= safeDuration; time++) {
    if (time > end) break
    times.push(time)
  }

  if (!times.length) times.push(Math.min(1, end || 1))

  return { times }
}

export const getExpectedClientThumbCount = (
  duration: number,
  maxThumbs: number = CLIENT_THUMB_CONFIG.coverThumbs
) => {
  return computeCoverThumbPlan(
    duration,
    maxThumbs
  ).times.length
}

const getResolutionScrubBonus = (
  width: number,
  height: number
) => {
  const pixels = width * height
  const bonus = CLIENT_THUMB_CONFIG.scrub.resolutionBonus

  if (pixels <= 1920 * 1080) return bonus.max1080p
  if (pixels <= 2560 * 1440) return bonus.max1440p
  if (pixels <= 3840 * 2160) return bonus.max4k

  return bonus.above4k
}

const getBaseScrubFrameCount = (
  duration: number,
  sourceWidth: number,
  sourceHeight: number
) => {
  const useLowPower4kPlan =
    is4kOrHigher(sourceWidth, sourceHeight) &&
    isLikelyLowPowerDevice()

  const buckets: readonly { maxSeconds: number; frames: number }[] =
    useLowPower4kPlan
      ? CLIENT_THUMB_CONFIG.scrub.lowPower4kBuckets
      : CLIENT_THUMB_CONFIG.scrub.durationBuckets

  return (
    buckets.find(bucket => duration <= bucket.maxSeconds)?.frames ??
    CLIENT_THUMB_CONFIG.scrub.minFrames
  )
}

export const getScrubFrameCount = (
  duration: number,
  sourceWidth: number,
  sourceHeight: number
) => {
  const lowPower4k =
    is4kOrHigher(sourceWidth, sourceHeight) &&
    isLikelyLowPowerDevice()

  const base = getBaseScrubFrameCount(
    duration,
    sourceWidth,
    sourceHeight
  )

  if (lowPower4k) {
    return clamp(
      base,
      CLIENT_THUMB_CONFIG.scrub.minFrames,
      CLIENT_THUMB_CONFIG.scrub.maxFrames
    )
  }

  const resolutionBonus = getResolutionScrubBonus(
    sourceWidth,
    sourceHeight
  )

  return clamp(
    base + resolutionBonus,
    CLIENT_THUMB_CONFIG.scrub.minFrames,
    CLIENT_THUMB_CONFIG.scrub.maxFrames
  )
}
/*
 * Scrub plan still contains every cover frame.
 *
 * Extra scrub-only frames are distributed evenly through
 * the gaps. Cover frames therefore continue to do double
 * duty and do not create duplicate seeks.
 */
const computeScrubThumbPlan = (
  duration: number,
  coverTimes: number[],
  sourceWidth: number,
  sourceHeight: number
): ThumbPlan => {
  const safeDuration = Math.max(0.1, duration)
  const start = Math.min(0.1, safeDuration)
  const end = Math.max(
    start,
    safeDuration - CLIENT_THUMB_CONFIG.seek.endPaddingSec
  )

  const desiredCount = Math.max(
    coverTimes.length,
    getScrubFrameCount(
      safeDuration,
      sourceWidth,
      sourceHeight
    )
  )

  const fixedTimes = [
    start,
    ...coverTimes,
    end
  ]

  const fixedKeys = new Set(
    fixedTimes.map(timeKey)
  )

  const extraCount = Math.max(
    0,
    desiredCount - fixedKeys.size
  )

  if (!extraCount) {
    return {
      times: Array.from(
        new Map(
          fixedTimes.map(time => [
            timeKey(time),
            time
          ])
        ).values()
      ).sort((a, b) => a - b)
    }
  }

  /*
   * Create extra scrub positions evenly across the ENTIRE
   * duration, rather than filling from the beginning.
   */
  const candidates = Array.from(
    { length: extraCount },
    (_, index) => {
      const ratio =
        (index + 1) /
        (extraCount + 1)

      return (
        start +
        (end - start) * ratio
      )
    }
  )

  const times = new Map<string, number>()

  fixedTimes.forEach(time => {
    const safeTime = clamp(
      time,
      start,
      end
    )

    times.set(
      timeKey(safeTime),
      safeTime
    )
  })

  candidates.forEach(time => {
    const safeTime = clamp(
      time,
      start,
      end
    )

    times.set(
      timeKey(safeTime),
      safeTime
    )
  })

  /*
   * If an evenly spaced candidate happened to land on the
   * same timestamp as a cover, top up with a denser global
   * distribution so we still reach desiredCount.
   */
  if (times.size < desiredCount) {
    const topUpCandidates = desiredCount * 4

    for (
      let index = 0;
      index < topUpCandidates &&
      times.size < desiredCount;
      index++
    ) {
      const ratio =
        (index + 1) /
        (topUpCandidates + 1)

      const time =
        start +
        (end - start) * ratio

      times.set(
        timeKey(time),
        time
      )
    }
  }

  return {
    times: Array.from(
      times.values()
    )
      .sort((a, b) => a - b)
      .slice(0, desiredCount)
  }
}
const buildTargets = (
  coverTimes: number[],
  scrubTimes: number[]
): FrameTarget[] => {
  const coverKeys = new Set(coverTimes.map(timeKey))
  const scrubKeys = new Set(scrubTimes.map(timeKey))
  const merged = new Map<string, number>()

  coverTimes.forEach(time =>
    merged.set(timeKey(time), time)
  )

  scrubTimes.forEach(time =>
    merged.set(timeKey(time), time)
  )

  return Array.from(merged.entries())
    .map(([key, time]) => ({
      key,
      time,
      isCover: coverKeys.has(key),
      isScrub: scrubKeys.has(key)
    }))
    .sort((a, b) => a.time - b.time)
}

const captureCurrentFrame = async ({
  video,
  canvas,
  context,
  width,
  height,
  quality,
  target
}: {
  video: HTMLVideoElement
  canvas: HTMLCanvasElement
  context: CanvasRenderingContext2D
  width: number
  height: number
  quality: number
  target: FrameTarget
}): Promise<{ frame: ClientFrame; kb: number }> => {
  context.drawImage(video, 0, 0, width, height)

  const blob = await canvasToBlob(canvas, quality)

  return {
    frame: {
      time: target.time,
      url: URL.createObjectURL(blob)
    },
    kb: Math.round(blob.size / 1024)
  }
}

const extractWithSeeks = async ({
  video,
  targets,
  canvas,
  context,
  width,
  height,
  quality,
  onCaptured
}: {
  video: HTMLVideoElement
  targets: FrameTarget[]
  canvas: HTMLCanvasElement
  context: CanvasRenderingContext2D
  width: number
  height: number
  quality: number
  onCaptured: (
    target: FrameTarget,
    frame: ClientFrame,
    index: number,
    kb: number,
    seekMs: number,
    renderMs: number
  ) => void
}) => {
  for (let index = 0; index < targets.length; index++) {
    const target = targets[index]
    let captured = false
    let lastError: unknown = null

    for (
      let attempt = 0;
      attempt < CLIENT_THUMB_CONFIG.retry.attempts;
      attempt++
    ) {
      try {
        const seekStartedAt = performance.now()
        await seekVideo(video, target.time)
        const seekMs = performance.now() - seekStartedAt

        const renderStartedAt = performance.now()
        const { frame, kb } = await captureCurrentFrame({
          video,
          canvas,
          context,
          width,
          height,
          quality,
          target
        })
        const renderMs = performance.now() - renderStartedAt

        onCaptured(
          target,
          frame,
          index,
          kb,
          seekMs,
          renderMs
        )

        captured = true
        break
      } catch (error) {
        lastError = error

        console.warn(
          '[CLIENT THUMBS] retrying failed frame',
          {
            index: index + 1,
            time: target.time,
            attempt: attempt + 1,
            error
          }
        )

        if (
          attempt <
          CLIENT_THUMB_CONFIG.retry.attempts - 1
        ) {
          await new Promise(resolve =>
            setTimeout(
              resolve,
              CLIENT_THUMB_CONFIG.retry.delayMs
            )
          )
        }
      }
    }

    if (!captured) {
      if (lastError instanceof Error) throw lastError

      throw new Error(
        `Failed to generate frame at ${target.time}`
      )
    }
  }
}

export const generateClientThumbs = async (
  file: File,
  duration?: number,
  options: GenerateClientThumbsOptions = {}
): Promise<ClientThumbs> => {
  const {
    maxThumbs = CLIENT_THUMB_CONFIG.coverThumbs,
    maxWidth = CLIENT_THUMB_CONFIG.output.maxWidth,
    maxHeight = CLIENT_THUMB_CONFIG.output.maxHeight,
    quality = CLIENT_THUMB_CONFIG.output.quality,
    onFrame
  } = options

  const totalStartedAt = performance.now()
  const video = document.createElement('video')

  video.muted = true
  video.playsInline = true
  video.preload = 'auto'

  const videoUrl = URL.createObjectURL(file)
  video.src = videoUrl

  const createdUrls = new Set<string>()

  try {
    if (
      video.readyState <
      HTMLMediaElement.HAVE_METADATA
    ) {
      await waitForEvent(
        video,
        'loadedmetadata',
        CLIENT_THUMB_CONFIG.metadataTimeoutMs
      )
    }

    const resolvedDuration =
      duration &&
      Number.isFinite(duration) &&
      duration > 0
        ? duration
        : video.duration

    if (
      !Number.isFinite(resolvedDuration) ||
      resolvedDuration <= 0
    ) {
      throw new Error('Invalid video duration')
    }

    const sourceWidth = video.videoWidth
    const sourceHeight = video.videoHeight

    if (!sourceWidth || !sourceHeight) {
      throw new Error('Video dimensions unavailable')
    }

    const { width, height } = getOutputSize(
      sourceWidth,
      sourceHeight,
      maxWidth,
      maxHeight
    )

    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height

    const context = canvas.getContext(
      '2d',
      { alpha: false }
    )

    if (!context) {
      throw new Error('Canvas context unavailable')
    }

    const coverPlan = computeCoverThumbPlan(
      resolvedDuration,
      maxThumbs
    )

    const scrubPlan = computeScrubThumbPlan(
      resolvedDuration,
      coverPlan.times,
      sourceWidth,
      sourceHeight
    )

    const targets = buildTargets(
      coverPlan.times,
      scrubPlan.times
    )

    const thumbs: ClientFrame[] = []
    const scrubFrames: ClientFrame[] = []

    console.time('[CLIENT THUMBS] total')

    console.log('[CLIENT THUMBS] start', {
      file: file.name,
      duration: resolvedDuration,
      source: `${sourceWidth}x${sourceHeight}`,
      output: `${width}x${height}`,
      covers: coverPlan.times.length,
      scrubTarget: getScrubFrameCount(
        resolvedDuration,
        sourceWidth,
        sourceHeight
      ),
      scrubs: scrubPlan.times.length,
      uniqueSeeks: targets.length,
      mode: 'seek'
    })

    const onCaptured = (
      target: FrameTarget,
      frame: ClientFrame,
      index: number,
      kb: number,
      seekMs: number,
      renderMs: number
    ) => {
      createdUrls.add(frame.url)

      if (target.isCover) thumbs.push(frame)
      if (target.isScrub) scrubFrames.push(frame)

      onFrame?.(
        frame,
        index,
        targets.length
      )

      console.log(
        '[CLIENT THUMBS] frame',
        {
          index: index + 1,
          total: targets.length,
          time: Number(
            target.time.toFixed(2)
          ),
          actualTime: Number(
            video.currentTime.toFixed(2)
          ),
          cover: target.isCover,
          scrub: target.isScrub,
          seekMs: Math.round(seekMs),
          renderMs: Math.round(renderMs),
          kb
        }
      )
    }

    await extractWithSeeks({
      video,
      targets,
      canvas,
      context,
      width,
      height,
      quality,
      onCaptured
    })

    thumbs.sort((a, b) => a.time - b.time)
    scrubFrames.sort((a, b) => a.time - b.time)

    console.timeEnd('[CLIENT THUMBS] total')

    console.log(
      '[CLIENT THUMBS] complete',
      {
        thumbs: thumbs.length,
        scrubFrames: scrubFrames.length,
        uniqueFrames: targets.length,
        totalMs: Math.round(
          performance.now() -
          totalStartedAt
        )
      }
    )

    return {
      thumbs,
      scrubFrames
    }
  } catch (error) {
    createdUrls.forEach(url => {
      try {
        URL.revokeObjectURL(url)
      } catch {}
    })

    throw error
  } finally {
    video.pause()
    video.removeAttribute('src')

    try {
      video.load()
    } catch {}

    URL.revokeObjectURL(videoUrl)
  }
}

export const revokeClientThumbs = (
  result?: ClientThumbs
) => {
  if (!result) return

  const urls = new Set(
    [
      ...result.thumbs,
      ...result.scrubFrames
    ].map(frame => frame.url)
  )

  urls.forEach(url => {
    if (!url.startsWith('blob:')) return

    try {
      URL.revokeObjectURL(url)
    } catch {}
  })
}

export const validateClientThumbs = async (
  generated: ClientThumbs,
  expectedThumbCount: number = CLIENT_THUMB_CONFIG.coverThumbs
): Promise<boolean> => {
  if (
    generated.thumbs.length !==
      expectedThumbCount ||
    generated.scrubFrames.length <
      generated.thumbs.length
  ) {
    return false
  }

  const seenUrls = new Set<string>()
  let previousTime = -1

  for (const frame of generated.thumbs) {
    if (
      !frame ||
      !Number.isFinite(frame.time) ||
      frame.time < 0 ||
      typeof frame.url !== 'string' ||
      !frame.url.startsWith('blob:') ||
      seenUrls.has(frame.url) ||
      frame.time <= previousTime
    ) {
      return false
    }

    seenUrls.add(frame.url)
    previousTime = frame.time
  }

  const results = await Promise.all(
    generated.thumbs.map(
      async frame =>
        await new Promise<boolean>(resolve => {
          const image = new Image()

          image.onload = () =>
            resolve(
              image.naturalWidth > 0 &&
              image.naturalHeight > 0
            )

          image.onerror = () =>
            resolve(false)

          image.src = frame.url
        })
    )
  )

  return results.every(Boolean)
}